/**
 * LiveActivityTicker — homepage band that rotates the latest release
 * line from each operated service.
 *
 * Why this exists:
 *   The corporate site federates JapanETCcard, JapanTollReceipts and
 *   NoJo Enterprise's own releases. Without a homepage signal, a
 *   visitor would have to click into /whats-new to discover anything
 *   new has shipped. This ticker surfaces the freshest line from each
 *   operated service in a quiet, auto-rotating band so the
 *   corporate site visibly feels actively operated.
 *
 * Behaviour:
 *   - Hits the same federation endpoint that /whats-new consumes —
 *     no extra polling cost.
 *   - Rotates one line every 6 seconds. Pauses on hover so visitors
 *     can read.
 *   - Each line is clickable and deep-links into the /whats-new page
 *     filtered to that service.
 *   - Hidden cleanly when the API is unreachable or when the dataset
 *     yields zero items — never shows an empty band.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowUpRight } from "lucide-react";
import { NOJO_CHANGELOG_ITEMS } from "@/data/nojoCorporateUpdates";
import { fetchJsonDeduped } from "@/lib/dedupedFetch";
import SourceFreshnessBadge from "@/components/SourceFreshnessBadge";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Match the canonical service order used elsewhere on the site.
const SERVICE_ORDER = [
  "nojo-enterprise",
  "japanetccard",
  "japantollreceipts",
  "japanchangemoney",
];

const SERVICE_SHORT = {
  "nojo-enterprise": "NoJo",
  japanetccard: "JEC",
  japantollreceipts: "JTR",
  japanchangemoney: "JCM",
};

// Vibrant brand-aligned palette (same set used in filter chips).
const SERVICE_ACCENT = {
  "nojo-enterprise": "#4F46E5", // indigo
  japanetccard: "#9333EA",     // purple
  japantollreceipts: "#16A34A", // green
  japanchangemoney: "#2563EB", // blue
};

function pickLang(v, lang) {
  if (!v) return "";
  if (typeof v === "string") return v;
  return v[lang] || v.en || v.ja || "";
}

export default function LiveActivityTicker({ lang }) {
  const isJa = lang === "ja";
  const [entries, setEntries] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [sourceFreshness, setSourceFreshness] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (!API_BASE) return undefined;
    // Pull a generous slice (covers more than 4 services even with
    // version-tiebreaker sort). Shares the in-flight de-dupe map with
    // every other hook on the page — if /whats-new is already fetching
    // this exact URL, we reuse that request instead of firing a second
    // one.
    fetchJsonDeduped(
      `${API_BASE}/api/integrations/ecosystem/changelog?lang=${lang}&pageSize=40`
    )
      .then((data) => {
        if (cancelled) return;
        // Merge API items (JEC/JTR) with NoJo client-side updates.
        const apiItems = (data?.items || []).map((it) => ({
          sid: it.serviceId || it._serviceId,
          version: it.version || "",
          title: pickLang(it.title, lang),
          sortDate: it.sortDate || it.publishedAtJst || it.date || "",
        }));
        const nojoItems = NOJO_CHANGELOG_ITEMS.map((it) => ({
          sid: it._serviceId || "nojo-enterprise",
          version: it.version || "",
          title: pickLang(it.title, lang),
          sortDate: it.sortDate || "",
        }));
        const allItems = [...apiItems, ...nojoItems].sort((a, b) => {
          const da = Date.parse(a.sortDate) || 0;
          const db = Date.parse(b.sortDate) || 0;
          return db - da;
        });
        // Pick the most recent item per service, in canonical order.
        const byService = new Map();
        for (const item of allItems) {
          if (!item.sid || byService.has(item.sid)) continue;
          byService.set(item.sid, item);
          if (byService.size >= SERVICE_ORDER.length) break;
        }
        const ordered = SERVICE_ORDER
          .filter((sid) => byService.has(sid))
          .map((sid) => byService.get(sid));
        if (cancelled) return;
        setEntries(ordered);
        setSourceFreshness(
          Array.isArray(data?.sourceFreshness) ? data.sourceFreshness : []
        );
      })
      .catch(() => {
        // API unreachable — still surface NoJo client-side updates.
        if (cancelled) return;
        const nojoItems = NOJO_CHANGELOG_ITEMS
          .slice(0, 1)
          .map((it) => ({
            sid: "nojo-enterprise",
            version: it.version || "",
            title: pickLang(it.title, lang),
          }));
        setEntries(nojoItems);
        setSourceFreshness([]);
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  // Auto-rotate every 6 seconds. Pauses on hover.
  useEffect(() => {
    if (entries.length < 2 || paused) return undefined;
    const handle = setInterval(() => {
      setIdx((i) => (i + 1) % entries.length);
    }, 6000);
    return () => clearInterval(handle);
  }, [entries.length, paused]);

  if (!entries.length) return null;

  const current = entries[idx % entries.length];
  const accent = SERVICE_ACCENT[current.sid] || "#475569";

  return (
    <section
      data-testid="live-activity-ticker"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative bg-white border-y border-[#E2E8F0]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex items-center gap-3 sm:gap-4 py-3">
        <Link
          to={`/${lang}/whats-new`}
          data-testid="live-activity-ticker-link"
          className="flex items-center gap-3 sm:gap-4 group flex-1 min-w-0"
        >
          <span
            aria-hidden
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] tracking-[0.18em] font-mono font-bold uppercase shrink-0"
            style={{ backgroundColor: `${accent}14`, color: accent }}
          >
            <span className="relative inline-flex h-1.5 w-1.5">
              <span
                className="absolute inline-flex h-full w-full rounded-full opacity-70 animate-ping"
                style={{ backgroundColor: accent }}
              />
              <span
                className="relative inline-flex h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: accent }}
              />
            </span>
            {isJa ? "ライブ" : "LIVE"}
          </span>
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-tight shrink-0"
            style={{ backgroundColor: `${accent}14`, color: accent }}
          >
            <span>{SERVICE_SHORT[current.sid] || current.sid}</span>
            {current.version && (
              <span className="font-mono opacity-90">{current.version}</span>
            )}
          </span>
          <p className="text-[13px] sm:text-[14px] text-slate-700 font-medium leading-snug truncate min-w-0 flex-1">
            {current.title}
          </p>
          <span className="hidden sm:inline-flex items-center gap-1 text-[11px] tracking-[0.16em] font-mono uppercase text-slate-400 shrink-0 group-hover:text-slate-700 transition-colors">
            <Activity className="h-3.5 w-3.5" strokeWidth={2} />
            {isJa ? "詳細を見る" : "See details"}
            <ArrowUpRight className="h-3 w-3" strokeWidth={2} />
          </span>
          {entries.length > 1 && (
            <span
              aria-hidden
              className="hidden md:inline-flex items-center gap-1 shrink-0"
            >
              {entries.map((_, i) => (
                <span
                  key={i}
                  className="h-1 w-1.5 rounded-full transition-colors"
                  style={{
                    backgroundColor:
                      i === idx % entries.length ? accent : "#CBD5E1",
                  }}
                />
              ))}
            </span>
          )}
        </Link>
        {/* Kept outside the <Link> — this badge is independently
            focusable (role="note" tabIndex=0) for screen-reader users,
            and a focusable widget must never nest inside another
            focusable element (anchor). Desktop-only: the ticker row is
            already tight on mobile/tablet widths. */}
        <SourceFreshnessBadge
          sourceFreshness={sourceFreshness}
          lang={lang}
          className="hidden lg:inline-flex shrink-0"
        />
      </div>
    </section>
  );
}
